import { CovideoGTMVideoAttributes, GTMVehicleAttributes } from '../../types';

export enum GTMVideoInteractionActions {
  VIEW = 'View',
  SEND_AND_SHARE = 'Send & Share',
  ANALYTICS = 'Analytics',
  COPY_VIDEO_URL = 'Copy Video URL',
  DUPLICATE = 'Duplicate',
  TRIM = 'Trim',
  MOVE = 'Move',
  COPY = 'Copy',
  DOWNLOAD = 'Download',
  PIN = 'Pin',
  MERGE = 'Merge',
  UNPIN = 'Unpin',
  ARCHIVE = 'Archive',
  UNARCHIVE = 'Unarchive',
  DELETE = 'Delete',
  ADD_TAGS = 'Add Tags',
  EDIT_VIDEO = 'Edit Video',
}

export enum GTMShareOption {
  COPY_VIDEO_URL = 'Copy Video URL',
  COPY_RAW_HTML = 'Copy Raw HTML',
}

export enum GTMSaveNewVideoInteractionType {
  RECORD = 'Record',
  UPLOAD = 'Upload',
  TRIM = 'Trim',
  MERGE = 'Merge',
  VOICE_RECORD = 'Voice Record',
}

export enum GTMVideoMessageInteractionAction {
  SHARE = 'Share',
  EMBED = 'Embed',
  SEND = 'Send',
  SCHEDULE = 'Schedule',
  PREVIEW = 'Preview',
}

type GTMVideoInteractionBase = {
  action: GTMVideoInteractionActions;
};

type GTMVideoInteractionWithIds = GTMVideoInteractionBase & {
  // When we have IDs, we can send the event with just this.
  videoIds: number[];

  // No details in this variant
  videoAttributes?: never;
  vehicleData?: never;
};

type GTMVideoInteractionWithDetails = GTMVideoInteractionBase & {
  // No IDs → we key off details instead
  videoIds?: undefined;

  // Both required in this branch
  videoAttributes?: CovideoGTMVideoAttributes;
  vehicleData?: GTMVehicleAttributes;
};

export type GTMVideoInteractonEventArguments =
  | GTMVideoInteractionWithIds
  | GTMVideoInteractionWithDetails;

export interface GTMSaveNewVideoInteractionEventArguments {
  videoAttributes: CovideoGTMVideoAttributes;
  type: GTMSaveNewVideoInteractionType;
  vehicleData?: GTMVehicleAttributes;
  mergedIds?: number[];
}

export type VideoAttributeInteractionInteractionEventArgs = {
  videoAttributes: CovideoGTMVideoAttributes;
  vehicleData?: GTMVehicleAttributes;
};

export type GTMVideoMessageInteractionActions =
  | {
      action: GTMVideoMessageInteractionAction.SHARE;
      shareOption: GTMShareOption; // required on Share
      videoMessageProperties: CovideoGTMVideoMessageProperties;
      video: CovideoGTMVideoAttributes;
      vehicleData?: GTMVehicleAttributes;
    }
  | {
      action:
        | GTMVideoMessageInteractionAction.EMBED
        | GTMVideoMessageInteractionAction.SEND
        | GTMVideoMessageInteractionAction.SCHEDULE
        | GTMVideoMessageInteractionAction.PREVIEW;
      videoMessageProperties: CovideoGTMVideoMessageProperties;
      shareOption?: never;
      video: CovideoGTMVideoAttributes;
      vehicleData?: GTMVehicleAttributes;
    };

/**
 * Describes the normalized data sent to GTM for "Video Message Interaction"
 * events that occur when a user shares or interacts with a video message.
 * Each field corresponds to a user-selected option within the share flow.
 */

export interface GTMVideoMessageProperties {
  customer_id?: string;
  sms?: boolean;
  email?: boolean;
  /**
   * Indicates whether the user opted to notify the recipient after sharing
   * the video message (e.g., "Notify viewer when video is viewed").
   *
   * @example true // user enabled notifications for video views
   */
  notification_viewed?: boolean;

  /**
   * The name or ID of the selected landing page template used for the
   * shared video. Typically derived from `templateId` or the template label.
   *
   * @example "Automotive Default Template"
   */
  landing_page?: string;

  /**
   * The name or ID of the selected Call-to-Action (CTA) link set
   * attached to the shared video. Usually maps to a `linksetId` or title.
   *
   * @example "Book a Test Drive"
   */
  call_to_action?: string;

  /**
   * The name or ID of the selected video thumbnail style or icon used
   * in the share configuration. Usually maps to `iconId` or template title.
   *
   * @example "Dark Overlay Thumbnail"
   */
  thumbnails?: string;
}

export interface CovideoGTMVideoMessageProperties {
  customerId?: string;
  sms?: boolean;
  email?: boolean;
  /**
   * Indicates whether the user opted to notify the recipient after sharing
   * the video message (e.g., "Notify viewer when video is viewed").
   *
   * @example true // user enabled notifications for video views
   */
  notificationViewed?: boolean;

  /**
   * The name or ID of the selected landing page template used for the
   * shared video. Typically derived from `templateId` or the template label.
   *
   * @example "Automotive Default Template"
   */
  landingPage?: string;

  /**
   * The name or ID of the selected Call-to-Action (CTA) link set
   * attached to the shared video. Usually maps to a `linksetId` or title.
   *
   * @example "Book a Test Drive"
   */
  callToAction?: string;

  /**
   * The name or ID of the selected video thumbnail style or icon used
   * in the share configuration. Usually maps to `iconId` or template title.
   *
   * @example "Dark Overlay Thumbnail"
   */
  thumbnails?: string;
}
